import { useQuery } from "@tanstack/react-query";

import { apiClient } from "./http-client";
import { queryClient } from "./query-client";
import {
  toWidgetApiError,
  widgetConfigQueryRetryDelay,
  widgetConfigQueryShouldRetry,
} from "./widget-api-error";

const STALE_TIME_MS = 1000 * 60 * 10;
const GC_TIME_MS = 1000 * 60 * 30;

interface CacheConfigOptions {
  client?: typeof queryClient;
  tokenVersion?: number;
  hasToken?: boolean;
}

interface ConfigQueryOptions {
  enabled?: boolean;
  token?: string;
  tokenVersion?: number;
}

/**
 * Creates a typed config loader for a widget product. Returns fetch, cache,
 * query, and invalidation helpers all sharing the same query-key namespace.
 *
 * @param productId - Unique product identifier used as the query-key prefix.
 * @param path      - API path to fetch the config from (including any query params).
 */
export function createWidgetConfigLoader<TConfig>(productId: string, path: string) {
  const QUERY_ROOT = ["config", productId] as const;

  function getQueryKey(tokenVersion = 0, hasToken = false) {
    return [...QUERY_ROOT, tokenVersion, hasToken] as const;
  }

  async function fetchConfig(token?: string): Promise<TConfig> {
    try {
      return await apiClient.get<TConfig>(path, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
    } catch (error) {
      throw await toWidgetApiError(error, `Failed to load ${productId} widget config`);
    }
  }

  function cacheConfig(
    config: TConfig,
    { client = queryClient, tokenVersion = 0, hasToken = false }: CacheConfigOptions = {},
  ) {
    client.setQueryData(getQueryKey(tokenVersion, hasToken), config);
  }

  function useConfigQuery({ enabled = true, token, tokenVersion = 0 }: ConfigQueryOptions = {}) {
    return useQuery({
      queryKey: getQueryKey(tokenVersion, Boolean(token)),
      queryFn: () => fetchConfig(token),
      enabled,
      placeholderData: (previousData) => previousData,
      staleTime: STALE_TIME_MS,
      gcTime: GC_TIME_MS,
      retry: widgetConfigQueryShouldRetry,
      retryDelay: widgetConfigQueryRetryDelay,
    });
  }

  function invalidateConfigQueries() {
    void queryClient.invalidateQueries({ queryKey: QUERY_ROOT });
  }

  return { QUERY_ROOT, fetchConfig, cacheConfig, useConfigQuery, invalidateConfigQueries } as const;
}
